/* ============================================================
   DELIVERY DATE & TIME PICKER
   File: assets/delivery-picker.js

   Reads settings from window.deliveryPickerSettings (output by
   snippets/cart-drawer.liquid via theme settings).

   Rules:
   - Harvested Today → earliest date is tomorrow (next calendar day)
   - Fresh Meat / Eggs → can book today if before cut-off time,
     otherwise tomorrow+
   - Blocked dates and max advance days are configurable per type
   - Selection is saved to cart attributes → becomes order attributes
   - Checkout is blocked until both date and time are chosen
   ============================================================ */

(function () {
  'use strict';

  /* ── Helpers ────────────────────────────────────────────────── */

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function toISO(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  function parseLines(str) {
    if (!str) return [];
    return str
      .split('\n')
      .map((s) => s.replace(/\r/g, '').trim())
      .filter(Boolean);
  }

  /* ── Cart API ───────────────────────────────────────────────── */

  async function fetchCart() {
    const resp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return resp.json();
  }

  async function saveCartAttributes(date, time) {
    try {
      // 1. Save to cart-level attributes (for admin visibility and legacy use)
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Delivery Date': date || '',
            'Delivery Time': time || '',
          },
        }),
      });

      // 2. Also write Delivery Date + Delivery Time as line item properties on
      //    every non-surcharge item, so the data appears on the customer-facing
      //    new-account order detail page (which reads line item properties, not
      //    cart attributes).
      //    /cart/change.js REPLACES properties entirely, so we merge with the
      //    item's existing props to preserve _product_type, _dtp_slot_ts, etc.
      const cart = await fetchCart();
      const itemsToUpdate = cart.items.filter(function (item) {
        return String(item.variant_id) !== '44854357721142'; // skip surcharge
      });

      // Shopify only allows one /cart/change.js in-flight at a time (it's
      // sequential internally), so we chain them rather than run in parallel.
      for (var i = 0; i < itemsToUpdate.length; i++) {
        var item = itemsToUpdate[i];
        var mergedProps = Object.assign({}, item.properties, {
          'Delivery Date': date || '',
          'Delivery Time': time || '',
        });
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          // IMPORTANT: quantity MUST be sent. /cart/change.js defaults the line
          // quantity to 1 when it is omitted, which silently wiped out any
          // quantity the customer had built up from the product card / cart.
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: mergedProps }),
        });
      }
    } catch (e) {
      console.error('[DeliveryPicker] Failed to save delivery details:', e);
    }
  }

  /* ── Cart type detection ────────────────────────────────────── */

  /*
   * Maps Shopify's native product_type field to the internal scheduler category.
   * This is the fallback used when _product_type is absent from line item
   * properties (e.g. "Buy Again" from the Shopify admin order page).
   *
   * Shopify product type → internal type:
   *   "HT Next Day"  → "Harvested Today"  (chicken, mutton)
   *   "FM Same Day"  → "Fresh Meat"        (fresh meat, eggs)
   */
  var SHOPIFY_TYPE_MAP = {
    'HT Next Day': 'Harvested Today',
    'FM Same Day': 'Fresh Meat'
  };

  function detectCartType(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return null;
    const types = cart.items
      .map((item) => {
        // 1. Prefer the explicit _product_type property set by buy-buttons.liquid
        const prop = item.properties && item.properties['_product_type'];
        if (prop && prop.trim()) return prop.trim();

        // 2. Fall back to mapping Shopify's native product_type — covers the
        //    "Buy Again" flow from the Shopify admin order page, where items
        //    are re-added without going through the product form so properties
        //    arrive empty.
        const shopifyType = item.product_type && item.product_type.trim();
        return (shopifyType && SHOPIFY_TYPE_MAP[shopifyType]) || '';
      })
      .filter(Boolean);

    // Harvested Today takes priority (shouldn't coexist with Fresh Meat, but just in case)
    if (types.includes('Harvested Today')) return 'Harvested Today';
    // Eggs follow Fresh Meat rules
    if (types.some((t) => t === 'Fresh Meat' || t === 'Eggs')) return 'Fresh Meat';
    return null;
  }

  /* ── Settings lookup ────────────────────────────────────────── */

  function getTypeSettings(type) {
    const s = window.deliveryPickerSettings;
    if (!s) return null;

    if (type === 'Harvested Today') {
      return {
        isHarvestedToday: true,
        timeSlots: parseLines(s.harvestedToday.timeSlots),
        blockedDates: parseLines(s.harvestedToday.blockedDates),
        advanceDays: parseInt(s.harvestedToday.advanceDays) || 14,
        leadTimeMins: parseInt(s.harvestedToday.leadTimeMins) || 0,
        blockedWeekdays: s.harvestedToday.blockedWeekdays || [false, true, false, false, false, false, false],
        cutoffTime: null,
      };
    }

    // Fresh Meat & Eggs
    return {
      isHarvestedToday: false,
      timeSlots: parseLines(s.freshMeat.timeSlots),
      blockedDates: parseLines(s.freshMeat.blockedDates),
      advanceDays: parseInt(s.freshMeat.advanceDays) || 14,
      leadTimeMins: parseInt(s.freshMeat.leadTimeMins) || 0,
      blockedWeekdays: s.freshMeat.blockedWeekdays || [false, false, false, false, false, false, false],
      cutoffTime: s.freshMeat.cutoffTime || '14:00',
    };
  }

  /* ── Time slot helpers ──────────────────────────────────────── */

  /*
   * Parse the START time from a slot string like "7:00 AM - 8:00 AM".
   * Returns a Date object set to that time on today's date, or null if
   * the string can't be parsed.
   */
  function parseSlotStartAsDate(slot) {
    const match = slot.match(/^\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d;
  }

  /*
   * Returns only the slots that are still bookable for the selected date.
   * For future dates all slots are shown as-is.
   * For today, a slot is hidden if:  now + leadTimeMins >= slotStart
   * (i.e. there isn't enough lead time left before the slot begins)
   */
  function filterSlotsForDate(slots, selectedISO, leadTimeMins) {
    const now = new Date();
    const todayISO = toISO(now);
    if (selectedISO !== todayISO) return slots; // future date — show everything

    const bufferMs = (parseInt(leadTimeMins) || 0) * 60 * 1000;

    return slots.filter((slot) => {
      const slotStart = parseSlotStartAsDate(slot);
      // If we can't parse the time, keep the slot (fail open)
      if (!slotStart) return true;
      // Slot is available only if there is still enough lead time
      return (now.getTime() + bufferMs) < slotStart.getTime();
    });
  }

  /*
   * Returns true if today would have at least one slot still bookable
   * given the configured lead time. Used when building the date list.
   */
  function todayHasRemainingSlots(slots, leadTimeMins) {
    return filterSlotsForDate(slots, toISO(new Date()), leadTimeMins).length > 0;
  }

  /* ── Date calculation ───────────────────────────────────────── */

  function isDateAllowed(date, blockedDates, blockedWeekdays) {
    // Check specific blocked dates (YYYY-MM-DD list)
    if (blockedDates.includes(toISO(date))) return false;
    // Check blocked weekday (0=Sun … 6=Sat)
    if (blockedWeekdays && blockedWeekdays[date.getDay()]) return false;
    return true;
  }

  function getAvailableDates(typeSettings) {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const { advanceDays, blockedDates, blockedWeekdays, isHarvestedToday, cutoffTime, timeSlots, leadTimeMins } = typeSettings;

    let startDate;

    if (isHarvestedToday) {
      // Always start from tomorrow — regardless of what time it is now
      startDate = new Date(todayStart);
      startDate.setDate(startDate.getDate() + 1);
    } else {
      // Fresh Meat / Eggs: today is allowed only if:
      //   a) current time is before the cut-off, AND
      //   b) at least one slot is still bookable (accounting for lead time)
      if (cutoffTime) {
        const [hh, mm] = cutoffTime.split(':').map(Number);
        const cutoffMoment = new Date(
          todayStart.getFullYear(),
          todayStart.getMonth(),
          todayStart.getDate(),
          hh,
          isNaN(mm) ? 0 : mm
        );
        const beforeCutoff = now < cutoffMoment;
        const slotsLeft = todayHasRemainingSlots(timeSlots, leadTimeMins);

        if (beforeCutoff && slotsLeft) {
          startDate = todayStart; // today is genuinely available
        } else {
          startDate = new Date(todayStart);
          startDate.setDate(startDate.getDate() + 1); // skip today
        }
      } else {
        // No cut-off configured — allow today only if slots remain
        const slotsLeft = todayHasRemainingSlots(timeSlots, leadTimeMins);
        startDate = new Date(todayStart);
        if (!slotsLeft) startDate.setDate(startDate.getDate() + 1);
      }
    }

    const endDate = new Date(todayStart);
    endDate.setDate(endDate.getDate() + advanceDays);

    const dates = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      if (isDateAllowed(d, blockedDates, blockedWeekdays)) {
        dates.push({
          iso: toISO(d),
          label: new Date(d).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        });
      }
    }

    return dates;
  }

  /* ── Picker DOM helpers ─────────────────────────────────────── */

  function populateDates(dateSelect, dates, savedDate) {
    dateSelect.innerHTML = '<option value="">— Select a date —</option>';
    dates.forEach(({ iso, label }) => {
      const opt = document.createElement('option');
      opt.value = iso;
      opt.textContent = label;
      dateSelect.appendChild(opt);
    });
    // Restore saved value if it's still a valid option
    if (savedDate && dateSelect.querySelector(`option[value="${savedDate}"]`)) {
      dateSelect.value = savedDate;
    }
  }

  function populateTimes(timeSelect, slots, selectedISO, savedTime, leadTimeMins) {
    // Filter out slots that are no longer bookable (past + lead time window)
    const availableSlots = filterSlotsForDate(slots, selectedISO, leadTimeMins);

    timeSelect.innerHTML = '<option value="">— Select a time slot —</option>';
    if (availableSlots && availableSlots.length) {
      availableSlots.forEach((slot) => {
        const opt = document.createElement('option');
        opt.value = slot;
        opt.textContent = slot;
        timeSelect.appendChild(opt);
      });
      timeSelect.disabled = false;
      if (savedTime && timeSelect.querySelector(`option[value="${savedTime}"]`)) {
        timeSelect.value = savedTime;
      }
    } else {
      timeSelect.disabled = true;
    }
  }

  /* ── Per-picker init ────────────────────────────────────────── */

  function initPickerElement(pickerEl, cart) {
    if (!pickerEl) return;

    const type = detectCartType(cart);
    const typeSettings = type ? getTypeSettings(type) : null;

    if (!type || !typeSettings) {
      pickerEl.hidden = true;
      return;
    }

    pickerEl.hidden = false;
    pickerEl.dataset.smaCartType = type;

    const dateSelect = pickerEl.querySelector('.sma-dp__date');
    const timeSelect = pickerEl.querySelector('.sma-dp__time');
    const errorEl = pickerEl.querySelector('.sma-dp__error');
    if (errorEl) errorEl.hidden = true;

    const attrs = cart.attributes || {};
    const savedDate = attrs['Delivery Date'] || '';
    const savedTime = attrs['Delivery Time'] || '';

    const dates = getAvailableDates(typeSettings);
    populateDates(dateSelect, dates, savedDate);

    if (dateSelect.value) {
      // A previously-saved date is still valid — restore time too (filtering past/lead-time slots for today)
      populateTimes(timeSelect, typeSettings.timeSlots, dateSelect.value, savedTime, typeSettings.leadTimeMins);
    } else {
      timeSelect.innerHTML = '<option value="">— Select a time slot —</option>';
      timeSelect.disabled = true;
      // If the saved date is no longer available, clear the cart attributes
      if (savedDate) saveCartAttributes('', '');
    }
  }

  /* ── Full refresh (both pickers) ────────────────────────────── */

  async function refreshAll() {
    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      return;
    }

    initPickerElement(document.querySelector('#sma-dp-drawer'), cart);
    initPickerElement(document.querySelector('#sma-dp-page'), cart);
  }

  /* ── Event delegation (attached once to document) ───────────── */

  let eventsAttached = false;

  function attachEvents() {
    if (eventsAttached) return;
    eventsAttached = true;

    /* Date selection */
    document.addEventListener('change', (e) => {
      if (!e.target.classList.contains('sma-dp__date')) return;
      const picker = e.target.closest('.sma-delivery-picker');
      if (!picker) return;

      const dateVal = e.target.value;
      const timeSelect = picker.querySelector('.sma-dp__time');
      const type = picker.dataset.smaCartType;
      const typeSettings = type ? getTypeSettings(type) : null;

      if (typeSettings) {
        // Pass the selected date and lead time so unbookable slots are filtered out
        populateTimes(timeSelect, typeSettings.timeSlots, dateVal, '', typeSettings.leadTimeMins);
      } else {
        timeSelect.innerHTML = '<option value="">— Select a time slot —</option>';
        timeSelect.disabled = true;
      }

      const errorEl = picker.querySelector('.sma-dp__error');
      if (errorEl) errorEl.hidden = true;

      // Save date immediately; clear any previous time
      saveCartAttributes(dateVal, '');
    });

    /* Time selection */
    document.addEventListener('change', (e) => {
      if (!e.target.classList.contains('sma-dp__time')) return;
      const picker = e.target.closest('.sma-delivery-picker');
      if (!picker) return;

      const dateVal = picker.querySelector('.sma-dp__date').value;
      const timeVal = e.target.value;

      const errorEl = picker.querySelector('.sma-dp__error');
      if (errorEl) errorEl.hidden = true;

      saveCartAttributes(dateVal, timeVal);
    });

    /* Checkout button interception — capture phase so we fire before Dawn */
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('#CartDrawer-Checkout, #checkout');
        if (!btn) return;

        const picker =
          btn.id === 'CartDrawer-Checkout'
            ? document.querySelector('#sma-dp-drawer')
            : document.querySelector('#sma-dp-page');

        // If picker is hidden (no schedulable product), let checkout proceed
        if (!picker || picker.hidden) return;

        const date = picker.querySelector('.sma-dp__date').value;
        const time = picker.querySelector('.sma-dp__time').value;

        if (!date || !time) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const errorEl = picker.querySelector('.sma-dp__error');
          if (errorEl) {
            errorEl.hidden = false;
          }
          picker.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      },
      true // capture phase
    );
  }

  /* ── Watch for cart mutations to keep pickers in sync ────────── */
  /*
   * When the cart drawer re-renders or cart page quantities change,
   * we want to refresh the pickers so the type/dates stay accurate.
   * We patch fetch to detect cart-mutating calls, same approach as
   * the old cart-dtp-validation.js.
   */
  function patchFetch() {
    const _orig = window.fetch;
    window.fetch = async function (...args) {
      const result = await _orig.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (/\/cart\/(add|change|clear)/.test(url)) {
        // Small delay to let Dawn re-render sections first
        setTimeout(refreshAll, 600);
      }
      return result;
    };
  }

  /* ── Bootstrap ───────────────────────────────────────────────── */

  function init() {
    attachEvents();
    patchFetch();
    refreshAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();